import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import * as Sentry from "@sentry/react";
import type { Dispatch, ReactNode, SetStateAction } from "react";

import { isLoggedIn } from "../auth/authUtils";
import { LogSentryException } from "../sentryLogger/sentryLogger";
import { useBaseTracker } from "../telemetry/BaseTrackingAdapter";
import type { RemoteConfig, RemoteConfigClient, RemoteConfigTraits } from "./remoteConfigClient";

export const remoteConfigTimeoutMS = 3000;

export interface RemoteConfigReactClient {
  identify: (
    userId: string,
    configurationType: "user" | "tracking",
    traits?: RemoteConfigTraits
  ) => Promise<void>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const RemoteConfigContext = createContext<{
  remoteConfigClient: RemoteConfigReactClient | undefined;
  remoteConfig: RemoteConfig;
  initialLoadFinished: boolean;
  isLoading: boolean;
}>({
  remoteConfig: {},
  remoteConfigClient: undefined,
  initialLoadFinished: false,
  isLoading: true,
});

const useInitialFetch = (
  identify: (userId: string, configurationType: "user" | "tracking") => Promise<void>
) => {
  const tracker = useBaseTracker();

  const initialLoadStartedRef = useRef(false);
  useEffect(() => {
    if (!isLoggedIn() && !initialLoadStartedRef.current) {
      const fetch = async () => {
        initialLoadStartedRef.current = true;
        try {
          await Promise.race([
            identify(tracker.getTrackingID(), "tracking"),
            new Promise((resolve) => setTimeout(resolve, remoteConfigTimeoutMS, "timeout")),
          ]);
        } catch (error) {
          LogSentryException({ messagePrefix: "Remote config error", error });
        }
      };
      fetch();
    }
  }, [identify, tracker]);
};

export const RemoteConfigProvider = forwardRef(
  (
    {
      children,
      remoteConfigClient,
    }: {
      children: ReactNode;
      remoteConfigClient: RemoteConfigClient;
    },
    ref
  ) => {
    const tracker = useBaseTracker();

    const [remoteConfig, setRemoteConfig] = useState<RemoteConfig>(
      remoteConfigClient.getAllConfig() ?? {}
    );

    useEffect(() => {
      Sentry.setContext("experiments", remoteConfig);
      tracker.setExperiments(remoteConfig);
    }, [remoteConfig, tracker]);

    const fetchRemoteConfig = useCallback(
      async (fetchFunction: () => void, configurationType: "user" | "tracking") => {
        const startTime = performance.now();
        await fetchFunction();
        const elapsedTime = performance.now() - startTime;

        const config = remoteConfigClient.getAllConfig() ?? {};
        tracker.setExperiments(config);
        setRemoteConfig(remoteConfigClient.getAllConfig() ?? {});

        tracker.trackRemoteConfigReceived({ duration: Math.round(elapsedTime), configurationType });
      },
      [tracker, remoteConfigClient]
    );

    const [initialLoadFinished, setInitialLoadFinished] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const identify = useCallback(
      async (
        userId: string,
        configurationType: "user" | "tracking",
        traits?: RemoteConfigTraits
      ) => {
        setIsLoading(true);
        await fetchRemoteConfig(
          remoteConfigClient.identify.bind(remoteConfigClient, userId, traits),
          configurationType
        );
        setInitialLoadFinished(true);
        setIsLoading(false);
      },
      [fetchRemoteConfig, remoteConfigClient]
    );
    const remoteConfigReactClient = useMemo(() => ({ identify, setIsLoading }), [identify]);

    useImperativeHandle(ref, () => remoteConfigReactClient, [remoteConfigReactClient]);

    useInitialFetch(identify);

    return (
      <RemoteConfigContext.Provider
        value={{
          remoteConfigClient: remoteConfigReactClient,
          remoteConfig,
          initialLoadFinished,
          isLoading,
        }}
      >
        {children}
      </RemoteConfigContext.Provider>
    );
  }
);

export const useRemoteConfigContext = () => {
  return useContext(RemoteConfigContext);
};

export const useRemoteConfig = (configName: string) => {
  const { remoteConfig, initialLoadFinished } = useContext(RemoteConfigContext);
  if (!initialLoadFinished) return undefined;
  return remoteConfig[configName] ?? (configName.endsWith("-ff") ? false : "control");
};

RemoteConfigProvider.displayName = "RemoteConfigProvider";
